import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "modalTitle", "form", "personIndex",
    "firstName", "lastName", "emailsContainer", "companyTitleContainer",
    "deleteSection", "linkSection", "linkSearchInput", "linkSearchResults",
    "editSection", "editTab", "linkTab", "unlinkSection", "linkedPersonInfo",
    "createNewSection"
  ]

  static values = {
    meetingId: Number,
    csrfToken: String
  }

  connect() {
    console.log("PersonEditController connected")
    console.log("Has modal target:", this.hasModalTarget)
    console.log("Has firstName target:", this.hasFirstNameTarget)
    console.log("Has lastName target:", this.hasLastNameTarget)
    console.log("Has personIndex target:", this.hasPersonIndexTarget)
    console.log("Has emailsContainer target:", this.hasEmailsContainerTarget)
    console.log("Has companyTitleContainer target:", this.hasCompanyTitleContainerTarget)
    
    // Close modal on escape key
    document.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      this.closeModal()
    }
  }

  openModal(event) {
    event.preventDefault()
    console.log("openModal called")
    
    const button = event.currentTarget
    console.log("Button:", button)
    
    const personIndex = button.dataset.personIndex
    console.log("Person Index:", personIndex)
    
    const personItem = button.closest(".extracted-person-item")
    console.log("Person Item:", personItem)
    
    if (!personItem) {
      console.error("Could not find .extracted-person-item parent")
      return
    }
    
    // Get person data from the list item's data attributes
    const name = personItem.dataset.personName || ""
    console.log("Person Name:", name)
    
    // Parse emails array (JSON)
    let emails = []
    try {
      emails = JSON.parse(personItem.dataset.personEmails || "[]")
    } catch (e) {
      console.warn("Failed to parse person emails:", e)
      emails = []
    }
    // Fallback to single email if array is empty
    if (emails.length === 0 && personItem.dataset.personEmail) {
      emails = [personItem.dataset.personEmail]
    }
    
    // Parse company/titles array (JSON)
    let companyTitles = []
    try {
      companyTitles = JSON.parse(personItem.dataset.personCompanyTitles || "[]")
    } catch (e) {
      console.warn("Failed to parse person company titles:", e)
      companyTitles = []
    }
    // Fallback to single company/title if array is empty
    if (companyTitles.length === 0) {
      const company = personItem.dataset.personCompany || ""
      const title = personItem.dataset.personTitle || ""
      if (company || title) {
        companyTitles = [{ company_name: company, title: title }]
      }
    }
    
    // Split name into first and last
    const nameParts = name.trim().split(/\s+/)
    const firstName = nameParts[0] || ""
    const lastName = nameParts.slice(1).join(" ") || ""
    
    // Set form values
    this.personIndexTarget.value = personIndex
    this.firstNameTarget.value = firstName
    this.lastNameTarget.value = lastName
    
    // Set modal title
    this.modalTitleTarget.textContent = name ? `Edit ${name}` : "Edit Person"
    
    // Show delete section for existing people
    if (this.hasDeleteSectionTarget) {
      this.deleteSectionTarget.classList.remove("hidden")
    }
    
    // Clear and populate emails
    this.emailsContainerTarget.innerHTML = ""
    if (emails.length > 0) {
      emails.forEach(email => this.addEmailField(email))
    } else {
      this.addEmailField("")
    }
    
    // Clear and populate company/title
    this.companyTitleContainerTarget.innerHTML = ""
    if (companyTitles.length > 0) {
      companyTitles.forEach(ct => {
        const companyName = ct.company_name || ct.companyName || ""
        const title = ct.title || ""
        this.addCompanyTitleField(companyName, title)
      })
    } else {
      this.addCompanyTitleField("", "")
    }
    
    // Check if person is linked to an existing record
    const linkedPersonId = personItem.dataset.linkedPersonId
    const linkedPersonName = personItem.dataset.linkedPersonName
    
    if (this.hasUnlinkSectionTarget) {
      if (linkedPersonId) {
        this.unlinkSectionTarget.classList.remove("hidden")
        if (this.hasLinkedPersonInfoTarget) {
          this.linkedPersonInfoTarget.textContent = linkedPersonName || "Unknown"
        }
      } else {
        this.unlinkSectionTarget.classList.add("hidden")
      }
    }
    
    // Show "Create as New" section if person is not linked and not already confirmed
    const isConfirmedNew = personItem.dataset.confirmedForCreation === "true"
    if (this.hasCreateNewSectionTarget) {
      if (linkedPersonId || isConfirmedNew) {
        this.createNewSectionTarget.classList.add("hidden")
      } else {
        this.createNewSectionTarget.classList.remove("hidden")
      }
    }
    
    // Reset to edit section and show modal
    this.showEditSection()
    this.modalTarget.classList.remove("hidden")
    this.firstNameTarget.focus()
  }

  addNew() {
    // Reset form for new person
    this.personIndexTarget.value = "-1" // -1 indicates new person
    this.firstNameTarget.value = ""
    this.lastNameTarget.value = ""
    
    this.modalTitleTarget.textContent = "Add New Person"
    
    // Hide delete section for new people
    if (this.hasDeleteSectionTarget) {
      this.deleteSectionTarget.classList.add("hidden")
    }
    
    // Hide unlink section for new people
    if (this.hasUnlinkSectionTarget) {
      this.unlinkSectionTarget.classList.add("hidden")
    }
    
    // Clear emails and add empty field
    this.emailsContainerTarget.innerHTML = ""
    this.addEmailField("")
    
    // Clear company/title and add empty field
    this.companyTitleContainerTarget.innerHTML = ""
    this.addCompanyTitleField("", "")
    
    // Show modal
    this.modalTarget.classList.remove("hidden")
    this.firstNameTarget.focus()
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
  }

  addEmail() {
    this.addEmailField("")
  }

  addEmailField(email = "") {
    const div = document.createElement("div")
    div.className = "flex items-center gap-2 email-row"
    div.innerHTML = `
      <input type="email" name="emails[]" value="${this.escapeHtml(email)}"
             class="flex-1 rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
             placeholder="email@example.com">
      <button type="button" class="text-slate-400 hover:text-red-600 remove-email-btn" data-action="click->person-edit#removeEmail">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `
    this.emailsContainerTarget.appendChild(div)
  }

  removeEmail(event) {
    const row = event.currentTarget.closest(".email-row")
    // Don't remove if it's the last email field
    if (this.emailsContainerTarget.querySelectorAll(".email-row").length > 1) {
      row.remove()
    } else {
      // Just clear the input
      row.querySelector("input").value = ""
    }
  }

  addCompanyTitle() {
    this.addCompanyTitleField("", "")
  }

  addCompanyTitleField(company = "", title = "") {
    const div = document.createElement("div")
    div.className = "bg-slate-50 rounded-lg p-3 company-title-row"
    div.innerHTML = `
      <div class="flex items-start gap-2">
        <div class="flex-1 space-y-2">
          <input type="text" name="companies[]" value="${this.escapeHtml(company)}"
                 class="w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                 placeholder="Company name">
          <input type="text" name="titles[]" value="${this.escapeHtml(title)}"
                 class="w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                 placeholder="Title/Role">
        </div>
        <button type="button" class="text-slate-400 hover:text-red-600 mt-2 remove-company-btn" data-action="click->person-edit#removeCompanyTitle">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `
    this.companyTitleContainerTarget.appendChild(div)
  }

  removeCompanyTitle(event) {
    const row = event.currentTarget.closest(".company-title-row")
    // Don't remove if it's the last row
    if (this.companyTitleContainerTarget.querySelectorAll(".company-title-row").length > 1) {
      row.remove()
    } else {
      // Just clear the inputs
      row.querySelectorAll("input").forEach(input => input.value = "")
    }
  }

  async save(event) {
    event.preventDefault()
    
    const personIndex = parseInt(this.personIndexTarget.value, 10)
    const firstName = this.firstNameTarget.value.trim()
    const lastName = this.lastNameTarget.value.trim()
    const name = [firstName, lastName].filter(Boolean).join(" ")
    
    // Collect emails
    const emailInputs = this.emailsContainerTarget.querySelectorAll("input[name='emails[]']")
    const emails = Array.from(emailInputs)
      .map(input => input.value.trim())
      .filter(Boolean)
    
    // Collect company/title pairs
    const companyInputs = this.companyTitleContainerTarget.querySelectorAll("input[name='companies[]']")
    const titleInputs = this.companyTitleContainerTarget.querySelectorAll("input[name='titles[]']")
    const companyTitles = []
    companyInputs.forEach((companyInput, idx) => {
      const company = companyInput.value.trim()
      const title = titleInputs[idx]?.value.trim() || ""
      if (company || title) {
        companyTitles.push({ company_name: company || "Unknown Company", title })
      }
    })
    
    // Validate
    if (!name) {
      alert("Please enter a name for this person.")
      return
    }
    
    // Build data payload
    const data = {
      person_index: personIndex,
      name: name,
      emails: emails,
      company_titles: companyTitles,
      // For backward compatibility, set primary email and first company/title
      email: emails[0] || "",
      company: companyTitles[0]?.company_name || "",
      title: companyTitles[0]?.title || ""
    }
    
    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/update_extracted_person`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      })
      
      if (response.ok) {
        const result = await response.json()
        // Check if this is a suggested person that will be added on approval
        const isSuggestedPerson = result.edited_for_addition === true
        
        if (isSuggestedPerson) {
          // For suggested persons, reload page to show updated UI state
          // This ensures the ✅ and "Will be added on approval" badge are shown
          window.location.reload()
        } else {
          this.updatePersonInList(personIndex, data, false)
          this.closeModal()
        }
      } else {
        const error = await response.json()
        alert(error.error || "Failed to save changes. Please try again.")
      }
    } catch (error) {
      console.error("Error saving person:", error)
      alert("Failed to save changes. Please try again.")
    }
  }

  async deletePerson() {
    const personIndex = parseInt(this.personIndexTarget.value, 10)
    
    if (!confirm("Are you sure you want to remove this person from the meeting?")) {
      return
    }
    
    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/delete_extracted_person`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({ person_index: personIndex })
      })
      
      if (response.ok) {
        // Remove person from list
        const personItem = document.querySelector(`[data-person-index="${personIndex}"]`)
        if (personItem) {
          personItem.remove()
        }
        this.closeModal()
        // Refresh page to update indices
        window.location.reload()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to remove person. Please try again.")
      }
    } catch (error) {
      console.error("Error deleting person:", error)
      alert("Failed to remove person. Please try again.")
    }
  }

  updatePersonInList(personIndex, data, forceIsSuggested = false) {
    if (personIndex === -1) {
      // New person added, reload page to show updated list
      window.location.reload()
      return
    }
    
    const personItem = document.querySelector(`[data-person-index="${personIndex}"]`)
    if (!personItem) return
    
    // Update data attributes (both old single-value and new JSON array formats)
    personItem.dataset.personName = data.name
    personItem.dataset.personEmail = data.email
    personItem.dataset.personCompany = data.company
    personItem.dataset.personTitle = data.title
    personItem.dataset.personEmails = JSON.stringify(data.emails || [])
    personItem.dataset.personCompanyTitles = JSON.stringify(data.company_titles || [])
    
    // Check if this is a suggested person (mentioned_only)
    const isSuggested = forceIsSuggested || 
                        personItem.dataset.isSuggested === "true" || 
                        personItem.classList.contains("suggested-person-item")
    
    if (isSuggested) {
      this.updateSuggestedPersonUI(personItem, data)
    } else {
      this.updateAttendeeUI(personItem, data)
    }
  }
  
  updateAttendeeUI(personItem, data) {
    // Update displayed content for regular attendees
    const nameEl = personItem.querySelector(".text-sm.font-medium.text-slate-900")
    if (nameEl) nameEl.textContent = data.name
    
    const emailEl = personItem.querySelector(".text-xs.text-slate-500")
    if (emailEl) {
      emailEl.textContent = data.email || ""
    }
    
    const roleCompanyEl = personItem.querySelector(".text-xs.text-slate-400")
    if (roleCompanyEl) {
      const parts = [data.title, data.company].filter(Boolean)
      roleCompanyEl.textContent = parts.join(" at ")
    }
    
    // Update avatar initial
    const avatar = personItem.querySelector(".w-10.h-10.rounded-full")
    if (avatar && data.name) {
      avatar.textContent = data.name.charAt(0).toUpperCase()
    }
  }
  
  updateSuggestedPersonUI(personItem, data) {
    // Mark as edited for addition
    personItem.dataset.isEdited = "true"
    
    // Update name display
    const nameEl = personItem.querySelector(".suggested-name") || personItem.querySelector(".text-sm.font-medium.text-slate-700")
    if (nameEl) nameEl.textContent = data.name
    
    // Update or create details line
    let detailsEl = personItem.querySelector(".suggested-details")
    const contextEl = personItem.querySelector(".suggested-context")
    const parts = [data.title, data.company].filter(Boolean)
    
    if (parts.length > 0) {
      if (!detailsEl) {
        // Create details element
        detailsEl = document.createElement("p")
        detailsEl.className = "text-xs text-slate-500 mt-0.5 suggested-details"
        const nameParent = nameEl?.parentElement
        if (nameParent && nameEl) {
          nameParent.insertBefore(detailsEl, nameEl.nextSibling)
        }
      }
      detailsEl.textContent = parts.join(" at ")
      
      // Hide context if showing details
      if (contextEl) contextEl.classList.add("hidden")
    }
    
    // Update avatar to green "confirmed" style
    const avatarContainer = personItem.querySelector(".relative")
    if (avatarContainer) {
      const avatar = avatarContainer.querySelector(".suggested-avatar") || avatarContainer.querySelector(".w-10.h-10.rounded-full")
      if (avatar) {
        // Change to green confirmed style
        avatar.className = "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0 bg-green-100 text-green-700 border-2 border-green-400 suggested-avatar"
        avatar.textContent = data.name.charAt(0).toUpperCase()
      }
      
      // Update icon to checkmark
      const iconSpan = avatarContainer.querySelector("span.absolute")
      if (iconSpan) {
        iconSpan.textContent = "✅"
        iconSpan.title = "Will be added on approval"
      }
    }
    
    // Update badge to green "Will be added" status
    const badge = personItem.querySelector(".suggested-badge")
    if (badge) {
      badge.className = "inline-flex items-center px-1.5 py-0.5 mt-1 rounded text-[10px] font-medium bg-green-100 text-green-700 suggested-badge"
      badge.innerHTML = `
        <svg class="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>
        Will be added on approval
      `
    }
    
    // Update edit button icon to pencil (edit) instead of plus (add)
    const editButton = personItem.querySelector("button[data-action='click->person-edit#openModal']")
    if (editButton) {
      editButton.title = "Edit details"
      const svg = editButton.querySelector("svg")
      if (svg) {
        svg.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />`
      }
    }
  }

  // Direct method to update a suggested person to "confirmed for addition" state
  updateSuggestedPersonToConfirmed(personIndex, data) {
    const personItem = document.querySelector(`[data-person-index="${personIndex}"]`)
    if (!personItem) {
      // If element not found, fall back to page reload
      window.location.reload()
      return
    }
    
    // Update data attributes
    personItem.dataset.personName = data.name || ""
    personItem.dataset.isEdited = "true"
    
    // Find and update the name element
    const nameEl = personItem.querySelector(".suggested-name")
    if (nameEl) {
      nameEl.textContent = data.name || ""
    }
    
    // Update or create details line (title at company)
    const parts = [data.title, data.company].filter(Boolean)
    let detailsEl = personItem.querySelector(".suggested-details")
    const contextEl = personItem.querySelector(".suggested-context")
    
    if (parts.length > 0) {
      if (!detailsEl && nameEl?.parentElement) {
        detailsEl = document.createElement("p")
        detailsEl.className = "text-xs text-slate-500 mt-0.5 suggested-details"
        nameEl.parentElement.insertBefore(detailsEl, nameEl.nextSibling)
      }
      if (detailsEl) {
        detailsEl.textContent = parts.join(" at ")
      }
      // Hide context when showing details
      if (contextEl) {
        contextEl.classList.add("hidden")
      }
    }
    
    // Update avatar to green confirmed style
    const avatar = personItem.querySelector(".suggested-avatar")
    if (avatar) {
      // Change from slate dashed border to solid green
      avatar.classList.remove("bg-slate-100", "text-slate-500", "border-dashed", "border-slate-300")
      avatar.classList.add("bg-green-100", "text-green-700", "border-green-400")
      if (data.name) {
        avatar.textContent = data.name.charAt(0).toUpperCase()
      }
    }
    
    // Update icon from 💬 to ✅
    const iconSpan = personItem.querySelector(".relative span.absolute")
    if (iconSpan) {
      iconSpan.textContent = "✅"
      iconSpan.title = "Will be added on approval"
    }
    
    // Update badge from amber "Not in meeting" to green "Will be added"
    const badge = personItem.querySelector(".suggested-badge")
    if (badge) {
      badge.classList.remove("bg-amber-100", "text-amber-700")
      badge.classList.add("bg-green-100", "text-green-700")
      badge.innerHTML = `
        <svg class="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
        Will be added on approval
      `
    }
    
    // Update edit button icon to pencil instead of plus
    const editBtn = personItem.querySelector("button[data-action='click->person-edit#openModal']")
    if (editBtn) {
      editBtn.title = "Edit details"
      const svg = editBtn.querySelector("svg")
      if (svg) {
        svg.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />`
      }
    }
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  // Show the link to existing person section
  showLinkSection() {
    // Update tab styling
    if (this.hasEditTabTarget) {
      this.editTabTarget.classList.remove("text-blue-600", "border-blue-600")
      this.editTabTarget.classList.add("text-slate-500")
    }
    if (this.hasLinkTabTarget) {
      this.linkTabTarget.classList.add("text-blue-600", "border-blue-600")
      this.linkTabTarget.classList.remove("text-slate-500")
    }
    
    // Show/hide sections
    if (this.hasEditSectionTarget) {
      this.editSectionTarget.classList.add("hidden")
    }
    if (this.hasLinkSectionTarget) {
      this.linkSectionTarget.classList.remove("hidden")
      if (this.hasLinkSearchInputTarget) {
        this.linkSearchInputTarget.focus()
        // Clear previous results
        this.linkSearchInputTarget.value = ""
        if (this.hasLinkSearchResultsTarget) {
          this.linkSearchResultsTarget.innerHTML = '<p class="text-sm text-slate-500 p-4 text-center">Type to search for existing contacts...</p>'
        }
      }
    }
  }

  // Show the edit section (hide link section)
  showEditSection() {
    // Update tab styling
    if (this.hasLinkTabTarget) {
      this.linkTabTarget.classList.remove("text-blue-600", "border-blue-600")
      this.linkTabTarget.classList.add("text-slate-500")
    }
    if (this.hasEditTabTarget) {
      this.editTabTarget.classList.add("text-blue-600", "border-blue-600")
      this.editTabTarget.classList.remove("text-slate-500")
    }
    
    // Show/hide sections
    if (this.hasLinkSectionTarget) {
      this.linkSectionTarget.classList.add("hidden")
    }
    if (this.hasEditSectionTarget) {
      this.editSectionTarget.classList.remove("hidden")
    }
  }

  // Search for existing people
  async searchPeople(event) {
    const query = event.target.value.trim()
    
    if (query.length < 2) {
      this.linkSearchResultsTarget.innerHTML = '<p class="text-sm text-slate-500 p-2">Type at least 2 characters to search...</p>'
      return
    }

    try {
      const response = await fetch(`/people/search?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        }
      })

      if (response.ok) {
        const people = await response.json()
        this.renderSearchResults(people)
      }
    } catch (error) {
      console.error("Error searching people:", error)
      this.linkSearchResultsTarget.innerHTML = '<p class="text-sm text-red-500 p-2">Error searching. Please try again.</p>'
    }
  }

  renderSearchResults(people) {
    if (people.length === 0) {
      this.linkSearchResultsTarget.innerHTML = '<p class="text-sm text-slate-500 p-2">No matching people found.</p>'
      return
    }

    const html = people.map(person => `
      <button type="button" 
              class="w-full text-left p-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-3"
              data-action="click->person-edit#selectPerson"
              data-person-id="${person.id}"
              data-person-name="${this.escapeHtml(person.name)}">
        ${person.avatar_url 
          ? `<img src="${person.avatar_url}" alt="${this.escapeHtml(person.name)}" class="w-10 h-10 rounded-full object-cover">`
          : `<div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700">${(person.name || '?').charAt(0).toUpperCase()}</div>`
        }
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-slate-900 truncate">${this.escapeHtml(person.name)}</p>
          <p class="text-xs text-slate-500 truncate">${this.escapeHtml(person.email || person.company || 'No details')}</p>
        </div>
      </button>
    `).join('')

    this.linkSearchResultsTarget.innerHTML = html
  }

  async selectPerson(event) {
    const button = event.currentTarget
    const personId = button.dataset.personId
    const personName = button.dataset.personName
    const personIndex = parseInt(this.personIndexTarget.value, 10)

    if (!confirm(`Link this extracted person to "${personName}"?`)) {
      return
    }

    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/link_extracted_person`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          person_index: personIndex,
          existing_person_id: personId
        })
      })

      if (response.ok) {
        this.closeModal()
        window.location.reload()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to link person. Please try again.")
      }
    } catch (error) {
      console.error("Error linking person:", error)
      alert("Failed to link person. Please try again.")
    }
  }

  // Confirm person as new contact (explicit confirmation)
  async confirmAsNew() {
    const personIndex = parseInt(this.personIndexTarget.value, 10)
    const firstName = this.firstNameTarget.value.trim()
    const lastName = this.lastNameTarget.value.trim()
    const name = [firstName, lastName].filter(Boolean).join(" ")
    
    // Collect emails
    const emailInputs = this.emailsContainerTarget.querySelectorAll("input[name='emails[]']")
    const emails = Array.from(emailInputs)
      .map(input => input.value.trim())
      .filter(Boolean)
    
    // Collect company/title pairs
    const companyInputs = this.companyTitleContainerTarget.querySelectorAll("input[name='companies[]']")
    const titleInputs = this.companyTitleContainerTarget.querySelectorAll("input[name='titles[]']")
    const companyTitles = []
    companyInputs.forEach((companyInput, idx) => {
      const company = companyInput.value.trim()
      const title = titleInputs[idx]?.value.trim() || ""
      if (company || title) {
        companyTitles.push({ company_name: company || "Unknown Company", title })
      }
    })
    
    // Validate
    if (!name) {
      alert("Please enter a name for this person.")
      return
    }
    
    // Build data payload with confirm_as_new flag
    const data = {
      person_index: personIndex,
      name: name,
      emails: emails,
      company_titles: companyTitles,
      email: emails[0] || "",
      company: companyTitles[0]?.company_name || "",
      title: companyTitles[0]?.title || "",
      confirm_as_new: true
    }
    
    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/update_extracted_person`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      })
      
      if (response.ok) {
        this.closeModal()
        // Reload to show updated status
        window.location.reload()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to save changes. Please try again.")
      }
    } catch (error) {
      console.error("Error confirming person as new:", error)
      alert("Failed to save changes. Please try again.")
    }
  }

  // Unlink an extracted person from an existing Person record
  async unlinkPerson() {
    const personIndex = parseInt(this.personIndexTarget.value, 10)

    if (!confirm("Are you sure you want to unlink this person? The AI-extracted details will be restored.")) {
      return
    }

    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/unlink_extracted_person`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({ person_index: personIndex })
      })

      if (response.ok) {
        this.closeModal()
        window.location.reload()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to unlink person. Please try again.")
      }
    } catch (error) {
      console.error("Error unlinking person:", error)
      alert("Failed to unlink person. Please try again.")
    }
  }
}

